import { LightningElement, registerDecorators as _registerDecorators, registerDecorators as _registerDecorators2, registerDecorators as _registerDecorators3 } from 'lwc';
import { WireI18n } from '~/modules/stores/i18nStore';
export function getNumberAttr(value, nullValue = 0) {
  if (value) {
    if (typeof value === 'string') {
      return Number.parseInt(value, 10);
    }
    return value;
  }
  return nullValue;
}
export function getObjectAttr(value, nullValue = null) {
  if (value) {
    if (typeof value == 'string') {
      try {
        return JSON.parse(value);
      } catch (error) {
        throw new Error(`Failed to initialize LWC component, the prop attribute '${value}' is not valid JSON.`,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        {
          cause: error
        });
      }
    }
    return value;
  }
  return nullValue;
}
export function getObjectOfType(value) {
  if (value) {
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch (error) {
        throw new Error(`Failed to initialize LWC component, the prop attribute '${value}' is not valid JSON.`);
      }
    }
    return value;
  }
  // @ts-expect-error * Automated Type Error Fix
  return undefined;
}
export function getBooleanAttr(value, nullValue = false) {
  if (value) {
    if (typeof value == 'string') {
      return value.toLocaleLowerCase() === 'true';
    }
    return value;
  }
  return nullValue;
}
export function isEmpty(value) {
  if (typeof value === 'undefined') {
    return true;
  } else if (typeof value === 'string') {
    return value.trim() === '';
  } else if (value instanceof Set) {
    return value.size <= 0;
  }
  throw new Error(`Unexpected type '${typeof value}' sent to isEmpty function, only strings and Sets are supported`);
}
export class BaseInput extends LightningElement {
  constructor(...args) {
    super(...args);
    this.name = 'input';
    this.label = void 0;
    this.ariaLabel = void 0;
    this._value = void 0;
    this._required = false;
    this._disabled = false;
    this._errorMessage = '';
    this.isError = false;
    this.tagLabel = '';
  }
  set forceShowError(value) {
    this._forceShowError = value;
    this.evaluateRequired();
  }
  get requiredMessage() {
    return this._requiredMessage || this.defaultRequiredMessage;
  }
  set requiredMessage(value) {
    this._requiredMessage = value;
  }
  i18nStoreCallback({
    formInputLabels
  }) {
    this.defaultRequiredMessage = formInputLabels.requiredMessage;
  }
  get forceShowError() {
    return this._forceShowError;
  }
  get labelCss() {
    if (this.required) {
      return 'label label--required';
    }
    return 'label';
  }
  get showTag() {
    return !!this.tagLabel;
  }
  get value() {
    return this._value;
  }
  set value(newValue) {
    this._value = newValue;
    if (newValue) {
      this.cleanError();
    } else if (this._required) {
      this.setError(this.requiredMessage);
    }
  }
  setError(message) {
    this._errorMessage = message;
    this.isError = true;
  }
  cleanError() {
    this._errorMessage = '';
    this.isError = false;
  }
  set required(value) {
    this._required = getBooleanAttr(value);
    this.evaluateRequired();
  }
  get required() {
    return !this._disabled && this._required;
  }
  set disabled(value) {
    this._disabled = getBooleanAttr(value);
    this.evaluateRequired();
  }
  get disabled() {
    return this._disabled;
  }
  get stateClassName() {
    return this.isError ? 'error' : '';
  }
  handleFocus() {
    this.cleanError();
  }
  handleBlur() {
    // Re-Dispacth Event
    this.dispatchEvent(new CustomEvent('blur', {
      bubbles: true,
      composed: true,
      cancelable: true
    }));
    if (!this._disabled && this._required && isEmpty(this._value)) {
      this.setError(this.requiredMessage);
    }
  }
  evaluateRequired() {
    if (!this._disabled && this._forceShowError && this._required && isEmpty(this._value)) {
      this.setError(this.requiredMessage);
    } else {
      this.cleanError();
    }
  }
  connectedCallback() {
    this.evaluateRequired();
  }
  /*LWC compiler v7.1.3*/
}
BaseInput.delegatesFocus = true;
_registerDecorators(BaseInput, {
  publicProps: {
    name: {
      config: 0
    },
    label: {
      config: 0
    },
    ariaLabel: {
      config: 0
    },
    tagLabel: {
      config: 0
    },
    forceShowError: {
      config: 3
    },
    requiredMessage: {
      config: 3
    },
    value: {
      config: 3
    },
    required: {
      config: 3
    },
    disabled: {
      config: 3
    }
  },
  publicMethods: ["setError", "cleanError"],
  track: {
    _value: 1
  },
  wire: {
    i18nStoreCallback: {
      adapter: WireI18n,
      method: 1,
      config: function ($cmp) {
        return {};
      }
    }
  },
  fields: ["_required", "_disabled", "_errorMessage", "isError"]
});
export class OptionsInput extends BaseInput {
  constructor(...args) {
    super(...args);
    this._options = void 0;
    this._multiple = false;
  }
  get multiple() {
    return this._multiple;
  }
  set multiple(value) {
    this._multiple = getBooleanAttr(value);
  }
  set options(value) {
    if (typeof value === 'string') {
      try {
        this._options = JSON.parse(value);
      } catch (error) {
        if (error instanceof SyntaxError) {
          this._options = [];
          value.split(',').forEach(word => {
            const trimmed = word.trim();
            this._options.push({
              value: trimmed,
              label: trimmed
            });
          });
        } else {
          throw error;
        }
      }
    } else {
      this._options = value;
    }
  }
  get options() {
    return this._options;
  }
  get transformedOptions() {
    const {
      options,
      value
    } = this;
    if (Array.isArray(options)) {
      const retValue = options.map((option, index) => ({
        ...option,
        key: `index-${index}`,
        checked: OptionsInput.checked(value, option)
      }));
      return retValue;
    }
    return [];
  }

  // @ts-expect-error * Automated Type Error Fix
  static checked(value, option) {
    return typeof value === 'string' && value === (option.value || option.label) || Array.isArray(value) && value.includes(option.value);
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators2(OptionsInput, {
  publicProps: {
    multiple: {
      config: 3
    },
    options: {
      config: 3
    }
  },
  track: {
    _options: 1
  },
  fields: ["_multiple"]
});
export class BaseMolecular extends LightningElement {
  constructor(...args) {
    super(...args);
    this._labels = {};
    this._isLoading = false;
    this.gridAxis = void 0;
    this.gridFlow = void 0;
    this.gridRowGap = void 0;
    this.gridGap = void 0;
    this.gridFlex = void 0;
    this.gridAlignItems = void 0;
    this.gridItemSize = void 0;
  }
  set labels(value) {
    this._labels = value;
  }
  get labels() {
    return this._labels;
  }
  set isLoading(value) {
    this._isLoading = getBooleanAttr(value);
  }
  get isLoading() {
    return this._isLoading;
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators3(BaseMolecular, {
  publicProps: {
    labels: {
      config: 3
    },
    isLoading: {
      config: 3
    },
    gridAxis: {
      config: 0
    },
    gridFlow: {
      config: 0
    },
    gridRowGap: {
      config: 0
    },
    gridGap: {
      config: 0
    },
    gridFlex: {
      config: 0
    },
    gridAlignItems: {
      config: 0
    },
    gridItemSize: {
      config: 0
    }
  },
  track: {
    _isLoading: 1
  },
  fields: ["_labels"]
});